import {Autocomplete, CircularProgress, TextField} from '@mui/material';
import {UIEvent, useState} from 'react';
import {Control, Controller, ControllerProps, FieldValues} from 'react-hook-form';

interface Props<T> {
  control: Control<any>;
  rules?: ControllerProps['rules'];
  onChange: (key: string, newValue: T | T[]) => void;
  inputValue: string;
  onInputChange: (newValue: string) => void;
  data: T[];
  loading: boolean;
  label?: string;
  placeholder?: string;
  getOptionLabel: (option: T) => string;
  name: string;
  multiple?: boolean;
  onScroll?: () => void;
}

export function AsyncSelectController<T>({
  control,
  rules = {},
  onChange,
  inputValue,
  onInputChange,
  data,
  loading,
  label,
  placeholder,
  getOptionLabel,
  name,
  multiple,
  onScroll,
}: Props<T>) {
  const [open, setOpen] = useState(false);

  return (
    <Controller
      control={control}
      rules={rules}
      name={name}
      render={({field: {value}, fieldState: {error}}) => (
        <Autocomplete
          open={open}
          multiple={multiple}
          onOpen={() => {
            setOpen(true);
          }}
          onClose={() => {
            setOpen(false);
          }}
          onChange={(_, newValue) => {
            onChange(name, newValue as T);
          }}
          value={value}
          getOptionLabel={getOptionLabel}
          inputValue={inputValue}
          onInputChange={(_, newInputValue) => {
            onInputChange(newInputValue);
          }}
          options={data}
          loading={loading}
          ListboxProps={{
            onScroll: (event: UIEvent<HTMLUListElement>) => {
              const target = event.currentTarget;
              const reachedBottom = target.scrollTop + target.clientHeight >= target.scrollHeight;
              if (reachedBottom) {
                onScroll?.();
              }
            },
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label={label}
              placeholder={placeholder}
              error={!!error}
              helperText={error ? error.message : ''}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loading ? <CircularProgress color="inherit" size={20} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />
      )}
    />
  );
}
